
import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Insight } from "@/types/insight";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { calculateReadingTime } from "@/utils/readingTime";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import InsightHeader from "@/components/insights/InsightHeader";
import InsightContent from "@/components/insights/InsightContent";
import InsightFooter from "@/components/insights/InsightFooter";
import SuggestedInsights from "@/components/insights/SuggestedInsights";
import SEOMetaTags from "@/components/SEOMetaTags";
import { localizePath } from "@/utils/languageRoutes";
import { getInsightCanonicalPaths } from "@/utils/insightSlugs";
import { blogTranslations } from "@/utils/translations/blog";

interface InsightDetailProps {
  language: string;
}

const LIKED_KEY = "duru_liked_insights";
const getLikedIds = (): string[] => {
  try { return JSON.parse(localStorage.getItem(LIKED_KEY) || "[]"); }
  catch { return []; }
};

const InsightDetail = ({ language }: InsightDetailProps) => {
  const { slug } = useParams();
  const { toast } = useToast();
  const location = useLocation();
  const t = blogTranslations[language as keyof typeof blogTranslations] || blogTranslations.EN;

  const [isLiked, setIsLiked] = useState(false);

  const { data: insight, isLoading } = useQuery({
    queryKey: ["insight", language, slug],
    queryFn: async () => {
      const slugFields = language === "KO"
        ? ["slug_ko", "slug", "slug_en"]
        : ["slug_en", "slug", "slug_ko"];

      const { data, error } = await supabase
        .from("insights")
        .select("*")
        .or(slugFields.map((field) => `${field}.eq.${slug}`).join(","))
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Insight not found");
      
      console.log('Raw insight content:', language === 'EN' ? data?.content_en : data?.content_ko);
      
      return data as Insight;
    },
  });

  // Sync isLiked from localStorage once insight id is known
  useEffect(() => {
    if (insight?.id) {
      setIsLiked(getLikedIds().includes(insight.id));
    }
  }, [insight?.id]);

  useEffect(() => {
    const incrementViewCount = async () => {
      if (!insight) return;

      const { error } = await supabase
        .rpc("increment_insight_views", { insight_id: insight.id });

      if (error) console.error("Error incrementing view count:", error);
    };

    incrementViewCount();
  }, [insight]);

  const handleLike = async () => {
    if (!insight) return;

    try {
      const nowLiked = !isLiked;
      const delta = nowLiked ? 1 : -1;

      const { error } = await supabase
        .rpc("increment_insight_likes", { insight_id: insight.id, delta });

      if (error) throw error;

      // Persist liked state in localStorage so it survives page refresh
      const liked = getLikedIds();
      const updated = nowLiked
        ? [...liked, insight.id]
        : liked.filter((id) => id !== insight.id);
      localStorage.setItem(LIKED_KEY, JSON.stringify(updated));

      setIsLiked(nowLiked);

      toast({
        title: nowLiked ? "Added to likes" : "Removed from likes",
        description: nowLiked ? "Article added to your likes" : "Article removed from your likes",
      });
    } catch (error) {
      console.error("Error updating likes:", error);
    }
  };

  const shareUrl = window.location.href;
  const shareTitle = language === 'EN' ? insight?.title_en || "" : insight?.title_ko || "";

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-pulse space-y-4">
          <div className="h-4 w-48 bg-gray-200 rounded"></div>
          <div className="h-4 w-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!insight) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-gray-500">Insight not found</p>
      </div>
    );
  }

  const readingTime = calculateReadingTime(language === 'EN' ? insight.content_en || '' : insight.content_ko || '');

  return (
    <>
      <SEOMetaTags
        language={language}
        path={location.pathname}
        pageType="article"
        article={{
          title: language === 'EN' ? insight.title_en || '' : insight.title_ko || '',
          description: language === 'EN'
            ? (insight.meta_description_en || insight.description_en || insight.title_en || '')
            : (insight.meta_description_ko || insight.description_ko || insight.title_ko || ''),
          publishedTime: insight.published_at || insight.created_at || '',
          modifiedTime: insight.updated_at || '',
          author: insight.author || "Duru Compliance Team",
          tags: insight.tags || [],
          thumbnailUrl: insight.thumbnail_url || undefined,
          readingTime: readingTime,
          canonicalPaths: getInsightCanonicalPaths(insight)
        }}
      />
      <main className="min-h-screen bg-white print:bg-white print:!pt-0">
      <div className="max-w-7xl mx-auto px-4">
        <div className="pt-20 print:!pt-0 lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-12 print:!block">
          {/* Main reading column */}
          <div className="min-w-0">
        <div className="pb-4 print:!pt-0">
          <div className="flex flex-col space-y-6">
            <Breadcrumb className="print:hidden">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={localizePath('/', language)}>Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={localizePath('/insights', language)}>Insights</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-normal text-muted-foreground">{language === 'EN' ? insight?.title_en : insight?.title_ko}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <h1 className="text-[32px] leading-[40px] sm:text-[42px] sm:leading-[52px] font-bold tracking-[-0.462px] text-[#242424] antialiased [text-rendering:optimizeLegibility] [overflow-wrap:break-word] [word-break:break-word] print:!mt-0">
              {language === 'EN' ? insight?.title_en : insight?.title_ko}
            </h1>

            <div className="flex items-center gap-3 text-sm text-gray-500">
              {insight.published_at && (
                <time dateTime={insight.published_at}>
                  {format(new Date(insight.published_at), "MMMM d, yyyy")}
                </time>
              )}
              <span className="text-gray-300">·</span>
              <span>{readingTime} min read</span>
            </div>
          </div>
        </div>

        <article className="py-2 print:!mt-0">
          <InsightHeader
            views_count={insight?.views_count}
            likes_count={insight?.likes_count}
            isLiked={isLiked}
            onLike={handleLike}
            url={shareUrl}
            title={shareTitle}
            thumbnailUrl={insight?.thumbnail_url ?? undefined}
          />

          {insight && (
            <div className="mt-8">
              <InsightContent 
                content={language === 'EN' ? insight.content_en || '' : insight.content_ko || ''} 
              />
            </div>
          )}

          <InsightFooter tags={insight?.tags || []} />
        </article>
          </div>

          {/* Sticky suggestions rail — desktop only. Scrolls internally if its
              content is taller than the viewport so the lower section stays reachable. */}
          <aside className="hidden lg:block print:hidden lg:pl-8">
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] divide-y divide-gray-200 overflow-y-auto pr-1 [scrollbar-width:thin]">
              <div className="pb-10">
                <h2 className="mb-5 text-lg font-semibold leading-7 text-[#242424] antialiased [text-rendering:optimizeLegibility]">
                  {t.sidebar.suggested}
                </h2>
                <SuggestedInsights
                  currentInsightId={insight?.id || ''}
                  language={language}
                  variant="sidebar"
                />
              </div>
              <div className="pt-10">
                <h2 className="mb-5 text-lg font-semibold leading-7 text-[#242424] antialiased [text-rendering:optimizeLegibility]">
                  {t.sidebar.mostRead}
                </h2>
                <SuggestedInsights
                  currentInsightId={insight?.id || ''}
                  language={language}
                  variant="sidebar"
                  sortBy="views"
                />
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Suggestions grid — mobile/tablet only (desktop uses the sticky rail) */}
      <section className="lg:hidden max-w-6xl mx-auto px-4 py-12 border-t border-gray-100 print:hidden">
        <h2 className="text-2xl font-bold mb-8">{t.sidebar.suggested}</h2>
        <SuggestedInsights currentInsightId={insight?.id || ''} language={language} />
      </section>
    </main>
    </>
  );
};

export default InsightDetail;
